import axios from 'axios'
import router from '@/router'
import { Message } from 'element-ui'

// 创建 axios 实例
const request = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器 - 添加 Token
request.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 错误消息映射
const errorMessages = {
  400: '请求参数错误',
  401: '未登录或登录已过期',
  403: '没有权限访问',
  404: '请求的资源不存在',
  428: '请先完成相关配置',
  500: '服务器内部错误',
  502: '网关错误',
  503: '服务暂不可用',
  504: '网关超时'
}

// 响应拦截器
request.interceptors.response.use(
  response => {
    const res = response.data
    if (res.code !== 0) {
      // 业务错误，显示提示
      Message.error(res.message || '操作失败')
      return Promise.reject(new Error(res.message || 'Error'))
    }
    return res
  },
  error => {
    const status = error.response?.status
    const data = error.response?.data

    // 获取错误消息
    let message = data?.message || errorMessages[status] || '请求失败，请稍后重试'

    // 处理不同状态码
    switch (status) {
      case 401:
        // 未授权 - 清除 Token 并跳转到登录页
        localStorage.removeItem('token')
        localStorage.removeItem('username')
        localStorage.removeItem('tokenExpire')
        Message.warning(message)
        router.push('/login')
        break

      case 428:
        // 配置未找到 - 提示用户配置
        Message.warning(message)
        break

      case 403:
        // 禁止访问
        Message.error(message)
        break

      case 400:
        // 参数错误
        Message.warning(message)
        break

      case 500:
      case 502:
      case 503:
      case 504:
        // 服务器错误
        Message.error(message)
        break

      default:
        // 其他错误
        if (error.message === 'Network Error') {
          message = '网络连接失败，请检查网络'
          Message.error(message)
        } else if (error.code === 'ECONNABORTED') {
          message = '请求超时，请稍后重试'
        }

    }

    // 将状态码和消息附加到错误对象
    error.statusCode = status
    error.errorMessage = message

    return Promise.reject(error)
  }
)

// ========== Auth API ==========
export function login(data) {
  return request.post('/auth/login', data)
}

export function register(data) {
  return request.post('/auth/register', data)
}

export function logout() {
  return request.post('/auth/logout')
}

export function checkAuth() {
  return request.get('/auth/check')
}

// ========== Menu API ==========
export function getMenuList() {
  return request.get('/menu/list')
}

export function getAllMenus() {
  return request.get('/menu/all')
}

// Menu Permission API
export function getUserList() {
  return request.get('/menu-permission/users')
}

export function getUserMenuPermissions(userId) {
  return request.get(`/menu-permission/user/${userId}`)
}

export function saveUserMenuPermissions(userId, menuIds) {
  return request.post(`/menu-permission/user/${userId}`, { menuIds })
}

export function addMenu(data) {
  return request.post('/menu/add', data)
}

export function updateMenu(data) {
  return request.post('/menu/update', data)
}

export function deleteMenu(id) {
  return request.post(`/menu/delete/${id}`)
}

// Dashboard API
export function getStats() {
  return request.get('/dashboard/stats')
}

export function getDashboardStats() {
  return request.get('/dashboard/stats')
}

export function getTradeLogs() {
  return request.get('/dashboard/logs')
}

export function updateBaseCapital(value) {
  return request.post(`/dashboard/config/base-capital?value=${value}`)
}

export function updateAutoTrade(enabled) {
  return request.post(`/dashboard/config/auto-trade?enabled=${enabled}`)
}

export function updateConfig(config) {
  // 分别更新各项配置
  const promises = []
  if (config.baseCapital !== undefined) {
    promises.push(request.post(`/dashboard/config/base-capital?value=${config.baseCapital}`))
  }
  if (config.isAutoTrade !== undefined) {
    promises.push(request.post(`/dashboard/config/auto-trade?enabled=${config.isAutoTrade}`))
  }
  if (config.isDebug !== undefined) {
    promises.push(request.post(`/dashboard/config/debug?enabled=${config.isDebug}`))
  }
  return Promise.all(promises)
}

// IP Whitelist API
export function getPendingIps() {
  return request.get('/ip/pending')
}

export function getAllIps() {
  return request.get('/ip/list')
}

export function approveIp(id) {
  return request.post(`/ip/approve/${id}`)
}

export function rejectIp(id) {
  return request.post(`/ip/reject/${id}`)
}

export function deleteIp(id) {
  return request.delete(`/ip/${id}`)
}

export function addIp(ipAddress, status) {
  return request.post('/ip/add', { ipAddress, status })
}

// Quote API (长桥行情)
export function getQuote(symbol) {
  return request.get(`/quote/${symbol}`)
}

export function getBatchQuotes(symbols) {
  return request.post('/quote/batch', { symbols })
}

export function getHotQuotes() {
  return request.get('/quote/hot')
}

export function getQuoteStatus() {
  return request.get('/quote/status')
}

export function getStockPickerData() {
  return request.get('/quote/picker')
}

export function searchStock(keyword) {
  return request.get('/quote/search', { params: { keyword } })
}

// Trade API (长桥交易)
export function submitLimitOrder(symbol, side, quantity, price) {
  return request.post('/trade/order/limit', { symbol, side, quantity, price })
}

export function submitMarketOrder(symbol, side, quantity) {
  return request.post('/trade/order/market', { symbol, side, quantity })
}

export function quickBuy(symbol, quantity) {
  return request.post('/trade/quick-buy', { symbol, quantity })
}

export function quickSell(symbol, quantity) {
  return request.post('/trade/quick-sell', { symbol, quantity })
}

export function cancelOrder(orderId) {
  return request.post(`/trade/order/cancel/${orderId}`)
}

export function getTodayOrders() {
  return request.get('/trade/orders/today')
}

export function getAccountBalance() {
  return request.get('/trade/account/balance')
}

export function getPositions() {
  return request.get('/trade/positions')
}

export function getLongPortTradeLogs() {
  return request.get('/trade/logs')
}

// ========== Webhook Trade Log API (交易日志) ==========
export function getWebhookTradeLogs(params) {
  return request.get('/webhook-trade-log/list', { params })
}

export function getWebhookTradeLogDetail(id) {
  return request.get(`/webhook-trade-log/${id}`)
}

export function getWebhookTodayStats() {
  return request.get('/webhook-trade-log/stats/today')
}

export function getWebhookDailyStats() {
  return request.get('/webhook-trade-log/stats/daily')
}

export function getWebhookSymbolStats() {
  return request.get('/webhook-trade-log/stats/symbol')
}

// Strategy API (策略配置)
export function getStrategyPositions() {
  return request.get('/strategy/positions')
}

export function generateStrategyScript(adjustments, usedCapital) {
  return request.post('/strategy/generate-script', { adjustments, usedCapital })
}

export function getStrategyQuote(symbol) {
  return request.get(`/strategy/quote/${symbol}`)
}

export function reconnectLongPort() {
  return request.post('/strategy/reconnect')
}

// ========== User Config API (用户配置) ==========
export function getLongPortConfig() {
  return request.get('/user/config/longport')
}

export function saveLongPortConfig(data) {
  return request.post('/user/config/longport', data)
}

export function checkLongPortConfig() {
  return request.get('/user/config/longport/check')
}

export function refreshLongPortConfig() {
  return request.post('/user/config/longport/refresh')
}

// ========== API Key Management (密钥管理) ==========
export function getApiKeys() {
  return request.get('/apikey/list')
}

export function createApiKey(data) {
  return request.post('/apikey/create', data)
}

export function updateApiKey(id, data) {
  return request.put(`/apikey/${id}`, data)
}

export function toggleApiKeyStatus(id) {
  return request.post(`/apikey/${id}/toggle`)
}

export function deleteApiKey(id) {
  return request.delete(`/apikey/${id}`)
}

// ========== Webhook Manage API (Webhook管理) ==========
export function getWebhookVariable() {
  return request.get('/webhook-manage/variable')
}

export function saveWebhookVariable(data) {
  return request.post('/webhook-manage/variable', data)
}

export function getWebhookLogs(params) {
  return request.get('/webhook-manage/logs', { params })
}

export function getWebhookSummary() {
  return request.get('/webhook-manage/stats/summary')
}

// ========== TradingView Debug Signal API ==========
export function getTvSignalLogs(params) {
  return request.get('/webhook-debug-log/tv-signals', { params })
}

export function getTvSignalLogDetail(id) {
  return request.get(`/webhook-debug-log/tv-signals/${id}`)
}

export function getTvSignalTodayStats() {
  return request.get('/webhook-debug-log/tv-signals/stats/today')
}

export function getTvSignalWatchlist(params) {
  return request.get('/webhook-debug-log/tv-signals/watchlist', { params })
}

export function queryTvSignals(data) {
  return request.post('/webhook-debug-log/tv-signals/query', data)
}

// ========== War Room API ==========
export function getWarRoomContext() {
  return request.get('/war-room/context')
}

export function getWarRoomDashboard() {
  return request.get('/war-room/dashboard')
}

export function getWarRoomMessages(limit = 50) {
  return request.get('/war-room/messages', { params: { limit } })
}

export function sendWarRoomText(text) {
  return request.post('/war-room/message/text', { text })
}

export function shareWarRoomTvSignal(data) {
  return request.post('/war-room/message/share-tv-signal', data)
}

export function shareWarRoomPosition(data) {
  return request.post('/war-room/message/share-position', data)
}

// ========== Trade Center API (交易中心) ==========

// 厂商配置
export function getVendorList() {
  return request.get('/trade/vendor/list')
}

export function getVendorConfig(vendorCode) {
  return request.get(`/trade/vendor/${vendorCode}`)
}

export function saveVendorConfig(data) {
  return request.post('/trade/vendor/save', data)
}

export function testVendorConnection(data) {
  return request.post('/trade/vendor/test', data)
}

export function updateVendorStatus(id, status) {
  return request.post(`/trade/vendor/${id}/status`, null, { params: { status } })
}

export function deleteVendorConfig(id) {
  return request.delete(`/trade/vendor/${id}`)
}

export function connectVendor(vendorCode) {
  return request.post(`/trade/vendor/${vendorCode}/connect`)
}

export function getFutuAccounts(host, port) {
  return request.post('/trade/vendor/futu/accounts', { host, port })
}

// FUTU 富途专用交易接口
export function getFutuBalance(trdMarket) {
  const params = trdMarket ? { trdMarket } : {}
  return request.get('/trade/futu/balance', { params })
}

export function getFutuPositions(trdMarket) {
  const params = trdMarket ? { trdMarket } : {}
  return request.get('/trade/futu/positions', { params })
}

export function getFutuQuote(symbol) {
  return request.get(`/trade/futu/quote/${symbol}`)
}

export function submitFutuOrder(order) {
  // order: { symbol, side, quantity, orderType, price? }
  if (order.orderType === 'market') {
    return request.post('/trade/futu/order/market', {
      symbol: order.symbol,
      side: order.side,
      quantity: order.quantity
    })
  } else {
    return request.post('/trade/futu/order/limit', {
      symbol: order.symbol,
      side: order.side,
      quantity: order.quantity,
      price: order.price
    })
  }
}

export function getFutuOrders() {
  return request.get('/trade/futu/orders/today')
}

export function getFutuTrades() {
  return request.get('/trade/futu/trades/today')
}

export function cancelFutuOrder(orderId) {
  return request.post(`/trade/futu/order/cancel/${orderId}`)
}

// ========== IB 盈透专用交易接口 ==========

// 连接管理
export function connectIB() {
  return request.post('/trade/ib/connect')
}

export function disconnectIB() {
  return request.post('/trade/ib/disconnect')
}

export function getIBStatus() {
  return request.get('/trade/ib/status')
}

export function testIBConnection(params) {
  return request.post('/trade/ib/test', params)
}

// 账户信息
export function getIBBalance() {
  return request.get('/trade/ib/account/balance')
}

export function getIBAccountSummary() {
  return request.get('/trade/ib/account/summary')
}

export function getIBPositions() {
  return request.get('/trade/ib/positions')
}

// 行情数据
export function getIBQuote(symbol) {
  return request.get(`/trade/ib/quote/${symbol}`)
}

export function getIBBatchQuotes(symbols) {
  return request.get('/trade/ib/quotes/batch', { params: { symbols } })
}

// 订单管理
export function submitIBOrder(order) {
  // order: { symbol, side, quantity, orderType, price?, stopPrice? }
  if (order.orderType === 'market') {
    return request.post('/trade/ib/order/market', {
      symbol: order.symbol,
      side: order.side,
      quantity: order.quantity
    })
  } else if (order.orderType === 'stop') {
    return request.post('/trade/ib/order/stop', {
      symbol: order.symbol,
      side: order.side,
      quantity: order.quantity,
      stopPrice: order.stopPrice
    })
  } else {
    return request.post('/trade/ib/order/limit', {
      symbol: order.symbol,
      side: order.side,
      quantity: order.quantity,
      price: order.price
    })
  }
}

export function getIBOrders() {
  return request.get('/trade/ib/orders/today')
}

export function cancelIBOrder(orderId) {
  return request.post(`/trade/ib/order/cancel/${orderId}`)
}

// ========== IB 配置管理 ==========

// 获取 IB 配置
export function getIBConfig() {
  return request.get('/ib-config')
}

// 更新交易模式 (0=正常, 1=固定股数, 2=比例计算)
export function updateIBTradeMode(mode) {
  return request.post('/ib-config/trade-mode', null, { params: { mode } })
}

// 更新干运行开关
export function updateIBDryRun(enabled) {
  return request.post('/ib-config/dry-run', null, { params: { enabled } })
}

// 更新基准资金
export function updateIBBaseCapital(value) {
  return request.post('/ib-config/base-capital', null, { params: { value } })
}

// 更新固定股数
export function updateIBFixedQty(value) {
  return request.post('/ib-config/fixed-qty', null, { params: { value } })
}

// 批量更新 IB 配置
export function updateIBConfigBatch(config) {
  return request.post('/ib-config/batch', config)
}

// 获取 IB 交易日志
export function getIBTradeLogs(params) {
  return request.get('/ib-config/logs', { params })
}

// 策略管理
export function getStrategies() {
  return request.get('/trade/strategy/list')
}

export function getStrategy(id) {
  return request.get(`/trade/strategy/${id}`)
}

export function saveStrategy(data) {
  return request.post('/trade/strategy/save', data)
}

export function updateStrategyEnabled(id, enabled) {
  return request.post(`/trade/strategy/${id}/enabled`, null, { params: { enabled } })
}

export function deleteStrategy(id) {
  return request.delete(`/trade/strategy/${id}`)
}

// 路由规则
export function getRouteRules() {
  return request.get('/trade/strategy/rule/list')
}

export function getStrategyRules(strategyId) {
  return request.get(`/trade/strategy/${strategyId}/rules`)
}

export function getRouteRule(id) {
  return request.get(`/trade/strategy/rule/${id}`)
}

export function saveRouteRule(data) {
  return request.post('/trade/strategy/rule/save', data)
}

export function updateRouteRuleEnabled(id, enabled) {
  return request.post(`/trade/strategy/rule/${id}/enabled`, null, { params: { enabled } })
}

export function updateRouteRulePriority(ids) {
  return request.post('/trade/strategy/rule/priority', ids)
}

export function deleteRouteRule(id) {
  return request.delete(`/trade/strategy/rule/${id}`)
}

// 统一交易
export function submitUnifiedOrder(order) {
  return request.post('/unified/order', order)
}

export function cancelUnifiedOrder(orderId, vendorCode) {
  return request.post('/unified/cancel', null, { params: { orderId, vendorCode } })
}

export function getUnifiedTodayOrders(vendorCode) {
  return request.get('/unified/orders/today', { params: { vendorCode } })
}

export function getUnifiedPositions(vendorCode) {
  return request.get('/unified/positions', { params: { vendorCode } })
}

export function getUnifiedBalance(vendorCode) {
  return request.get('/unified/balance', { params: { vendorCode } })
}

export function getUnifiedQuote(symbol, vendorCode) {
  return request.get(`/unified/quote/${symbol}`, { params: { vendorCode } })
}

// ========== THS Position API (同花顺持仓) ==========
export function getThsPositions() {
  return request.get('/ths-position/list')
}

export function addThsPosition(data) {
  return request.post('/ths-position/add', data)
}

export function updateThsPosition(data) {
  return request.post('/ths-position/update', data)
}

export function deleteThsPosition(id) {
  return request.delete(`/ths-position/delete/${id}`)
}

export function batchSaveThsPositions(positions) {
  return request.post('/ths-position/batch-save', positions)
}

// ================== 信号预览 ==================
export function getSignalPreviewPending() {
  return request.get('/signal-preview/pending')
}

export function executeSignalPreview(id, amount, fullPosition = false, price = null, quantity = null) {
  return request.post(`/signal-preview/execute/${id}`, { amount, fullPosition, price, quantity })
}

export function cancelSignalPreview(id) {
  return request.post(`/signal-preview/cancel/${id}`)
}

export function getSignalPreviewPrice(symbol) {
  return request.get(`/signal-preview/price/${symbol}`)
}

export function getSignalPreviewHistory(limit = 50) {
  return request.get(`/signal-preview/history?limit=${limit}`)
}

// ================== QMT 国金证券 ==================
export function getQmtMarketTrend() {
  return request.get('/qmt/market-trend')
}

export function getQmtSectorStocks(sectorCode) {
  // ETF代码不需要编码（如 512880.SH）
  return request.get(`/qmt/market-trend/sector/${sectorCode}`)
}

export function getQmtWatchlistStocks() {
  return request.get('/qmt/market-trend/watchlist')
}

export function getQmtStatus() {
  return request.get('/qmt/status')
}

export function getQmtQuote(stockCode) {
  return request.get(`/qmt/quote/${stockCode}`)
}

export function placeQmtOrder(orderData) {
  return request.post('/qmt/order', orderData)
}

export function placeQmtBatchOrders(batchData) {
  return request.post('/qmt/order/batch', batchData)
}

export function cancelQmtOrder(orderId) {
  return request.post(`/qmt/cancel/${orderId}`)
}

export function getQmtBalance() {
  return request.get('/qmt/balance')
}

export function getQmtPositions() {
  return request.get('/qmt/positions')
}

export function getQmtOrders() {
  return request.get('/qmt/orders')
}

export function getQmtStockNames(codes) {
  return request.post('/qmt/stock-names', { codes })
}

export function searchQmtStocks(keyword) {
  return request.get('/qmt/stock-search', { params: { keyword } })
}

// QMT A股热力（读 T_USER_CHINA_STOCK，行情来自 QMT batch-quote）
export function getQmtSectorHeat() {
  return request.get('/qmt/sector-heat')
}

// QMT 技术指标信号（RSI / MACD / ADX / 影线）
export function postQmtBatchSignals(codes) {
  return request.post('/qmt/batch-signals', { codes })
}

// ================== QMT A 股自定义组合（标签） ==================
export function getQmtStockGroups() {
  return request.get('/qmt-stock-group/groups')
}

export function createQmtStockGroup(data) {
  return request.post('/qmt-stock-group/groups', data)
}

export function updateQmtStockGroup(id, data) {
  return request.put(`/qmt-stock-group/groups/${id}`, data)
}

export function deleteQmtStockGroup(id) {
  return request.delete(`/qmt-stock-group/groups/${id}`)
}

export function getQmtStockGroupItems() {
  return request.get('/qmt-stock-group/items')
}

export function assignQmtStockGroup(stockCode, groupId) {
  return request.post('/qmt-stock-group/assign', { stockCode, groupId })
}

export function removeQmtStockGroupItem(stockCode) {
  return request.delete('/qmt-stock-group/items', { params: { stockCode } })
}

export function getQmtPresetOrders() {
  return request.get('/qmt-preset-order/list')
}

export function createQmtPresetOrder(data) {
  return request.post('/qmt-preset-order', data)
}

export function cancelQmtPresetOrder(id) {
  return request.post(`/qmt-preset-order/cancel/${id}`)
}

export function executeQmtPresetOrder(id) {
  return request.post(`/qmt-preset-order/execute/${id}`)
}


// ================== 自选股管理 ==================
export function getWatchlist() {
  return request.get('/watchlist')
}

export function getWatchlistCodes() {
  return request.get('/watchlist/codes')
}

export function getWatchlistSectorHeat() {
  return request.get('/watchlist/sector-heat')
}

export function getWatchlistLowAbsorb(params) {
  return request.get('/watchlist/low-absorb', { params })
}

export function getWatchlistNews(params) {
  return request.get('/watchlist/news', { params })
}

export function analyzeWatchlistNews(data) {
  return request.post('/watchlist/news/analyze', data)
}

export function addToWatchlist(stockCode, stockName, stockModel) {
  return request.post('/watchlist', { stockCode, stockName, stockModel })
}

export function removeFromWatchlist(stockCode) {
  return request.delete(`/watchlist/${encodeURIComponent(stockCode)}`)
}

// 批量买入（热力图）
export function watchlistBatchBuy(payload) {
  return request.post('/watchlist/batch-buy', payload)
}

// 批量限价买入（ATR偏移）
export function watchlistBatchBuyLimit(payload) {
  return request.post('/watchlist/batch-buy-limit', payload)
}

// 获取今日批量买入的订单号列表
export function getBatchBuyOrderIds() {
  return request.get('/watchlist/batch-buy/order-ids')
}

// ================== A股自选股管理 ==================
export function getChinaStockList() {
  return request.get('/china-stock')
}

export function addToChinaStock(stockCode, stockName, stockModel) {
  return request.post('/china-stock', { stockCode, stockName, stockModel })
}

export function updateChinaStock(stockCode, stockName, stockModel) {
  return request.put(`/china-stock/${encodeURIComponent(stockCode)}`, { stockName, stockModel })
}

export function removeFromChinaStock(stockCode) {
  return request.delete(`/china-stock/${encodeURIComponent(stockCode)}`)
}

export function getChinaStockSectors() {
  return request.get('/china-stock/sectors')
}

export function getChinaStockLowAbsorb(params) {
  return request.get('/china-stock/low-absorb', { params })
}

// ========== Portfolio 综合持仓汇总 API ==========

export function getAggregatePortfolio(params) {
  return request.get('/portfolio/aggregate', { params })
}

export function getDisabledAggregatePortfolio(includePositions = false) {
  return request.get('/portfolio/aggregate-disabled', { params: { includePositions } })
}

// 杠杆部分盈亏汇总（按 server 分组）
// 被排除组合从后端 T_SYS_CONFIG 读取, 由 leverage-excluded-portfolios 接口维护
export function getLeveragePnl(serverIds) {
  const params = {}
  if (Array.isArray(serverIds) && serverIds.length) {
    params.serverIds = serverIds.join(',')
  } else if (typeof serverIds === 'string' && serverIds.trim()) {
    params.serverIds = serverIds
  }
  return request.get('/portfolio/leverage-pnl', { params })
}

// 获取杠杆部分卖出功能中被排除的组合 ID 列表
export function getLeverageExcludedPortfolios() {
  return request.get('/portfolio/leverage-excluded-portfolios')
}

// 更新杠杆部分卖出功能中被排除的组合 ID 列表
export function updateLeverageExcludedPortfolios(ids) {
  return request.post('/portfolio/leverage-excluded-portfolios', { ids: Array.isArray(ids) ? ids : [] })
}

export function getExchangeRates() {
  return request.get('/portfolio/exchange-rates')
}

export function getPortfolioTakeProfitConfig() {
  return request.get('/portfolio/take-profit-config')
}

export function savePortfolioTakeProfitConfig(data) {
  return request.put('/portfolio/take-profit-config', data)
}

export function tryLockPortfolioTakeProfit(data) {
  return request.post('/portfolio/take-profit-config/try-lock', data)
}

export function finishPortfolioTakeProfit(data) {
  return request.post('/portfolio/take-profit-config/finish', data)
}

// ========== 服务器自动平仓（按服务器维度的 Day PnL % 阈值） ==========
export function listAutoCloseServerConfigs() {
  return request.get('/portfolio/auto-close-server')
}

export function getAutoCloseServerConfig(serverId) {
  return request.get(`/portfolio/auto-close-server/${serverId}`)
}

export function saveAutoCloseServerConfig(data) {
  return request.put('/portfolio/auto-close-server', data)
}

export function deleteAutoCloseServerConfig(serverId) {
  return request.delete(`/portfolio/auto-close-server/${serverId}`)
}

export function tryLockAutoCloseServer(data) {
  return request.post('/portfolio/auto-close-server/try-lock', data)
}

export function finishAutoCloseServer(data) {
  return request.post('/portfolio/auto-close-server/finish', data)
}

export function getAutoCloseServerTriggerLogs(params) {
  return request.get('/portfolio/auto-close-server/trigger-logs', { params })
}

export function getPortfolioPositions(broker, accId) {
  return request.get('/portfolio/positions', { params: { broker, accId } })
}

export function getMiddlewareServers() {
  return request.get('/portfolio/servers')
}

export function addMiddlewareServer(data) {
  return request.post('/portfolio/servers', data)
}

export function updateMiddlewareServer(id, data) {
  return request.put(`/portfolio/servers/${id}`, data)
}

export function deleteMiddlewareServer(id) {
  return request.delete(`/portfolio/servers/${id}`)
}

// ========== 持仓卡片 API（移动端 dashboard 固定监控） ==========

export function getPositionsCardConfig() {
  return request.get('/positions-card/config')
}

export function getPositionsCard(url, broker) {
  return request.get('/positions-card', { params: { url, broker } })
}

export function getPositionsCardRebalanceHistory(accId, count = 8) {
  return request.get('/positions-card/rebalance-history', { params: { accId, count } })
}

// ========== 服务器远程配置 API ==========

// 获取完整配置
export function getServerBrokerConfig(serverId) {
  return request.get(`/portfolio/servers/${serverId}/proxy/webhook/broker-config`)
}

// 设置 Webhook 开关
export function setWebhookEnabled(serverId, enabled) {
  return request.put(`/portfolio/servers/${serverId}/proxy/webhook/webhook-enabled`, { enabled })
}

// 获取策略路由
export function getMarketRoutes(serverId) {
  return request.get(`/portfolio/servers/${serverId}/proxy/webhook/market-routes`)
}

// 新增/修改策略路由
export function updateMarketRoute(serverId, marketId, data) {
  return request.put(`/portfolio/servers/${serverId}/proxy/webhook/market-routes/${marketId}`, data)
}

// 删除策略路由
export function deleteMarketRoute(serverId, marketId) {
  return request.delete(`/portfolio/servers/${serverId}/proxy/webhook/market-routes/${marketId}`)
}

// 获取黑名单
export function getBlacklist(serverId) {
  return request.get(`/portfolio/servers/${serverId}/proxy/webhook/blacklist`)
}

// 添加到黑名单
export function addToBlacklist(serverId, symbol) {
  return request.post(`/portfolio/servers/${serverId}/proxy/webhook/blacklist/add`, { symbol })
}

// 从黑名单移除
export function removeFromBlacklist(serverId, symbol) {
  return request.post(`/portfolio/servers/${serverId}/proxy/webhook/blacklist/remove`, { symbol })
}

// 获取卖出锁定列表
export function getSellLock(serverId) {
  return request.get(`/portfolio/servers/${serverId}/proxy/webhook/sell-lock`)
}

// 添加卖出锁定
export function addToSellLock(serverId, symbol) {
  return request.post(`/portfolio/servers/${serverId}/proxy/webhook/sell-lock/add`, { symbol })
}

// 移除卖出锁定
export function removeFromSellLock(serverId, symbol) {
  return request.post(`/portfolio/servers/${serverId}/proxy/webhook/sell-lock/remove`, { symbol })
}

// 获取券商连接状态
export function getBrokersStatus(serverId) {
  return request.get(`/portfolio/servers/${serverId}/proxy/webhook/brokers/status`)
}

// 获取组合仓位追踪列表
export function getPortfolioTrackingList(serverId, params) {
  return request.get(`/portfolio/servers/${serverId}/proxy/portfolio/`, { params })
}

// 获取单条组合仓位追踪
export function getPortfolioTrackingItem(serverId, marketId, brokerId, symbol) {
  return request.get(`/portfolio/servers/${serverId}/proxy/portfolio/${encodeURIComponent(marketId)}/${encodeURIComponent(brokerId)}/${encodeURIComponent(symbol)}`)
}

// 新增 / 累加组合仓位追踪
export function createPortfolioTrackingItem(serverId, data) {
  return request.post(`/portfolio/servers/${serverId}/proxy/portfolio/`, data)
}

// 覆盖式更新组合仓位追踪
export function updatePortfolioTrackingItem(serverId, marketId, brokerId, symbol, data) {
  return request.put(`/portfolio/servers/${serverId}/proxy/portfolio/${encodeURIComponent(marketId)}/${encodeURIComponent(brokerId)}/${encodeURIComponent(symbol)}`, data)
}

// 删除单条组合仓位追踪
export function deletePortfolioTrackingItem(serverId, marketId, brokerId, symbol) {
  return request.delete(`/portfolio/servers/${serverId}/proxy/portfolio/${encodeURIComponent(marketId)}/${encodeURIComponent(brokerId)}/${encodeURIComponent(symbol)}`)
}

// ========== 服务器 IB 订单管理 API ==========

// 获取综合订单-当日（前端仅展示 IB）
export function getServerUnifiedTodayOrders(serverId) {
  return request.get(`/portfolio/servers/${serverId}/proxy/unified-orders/today`)
}

// 获取综合订单-历史（前端仅展示 IB）
export function getServerUnifiedHistoryOrders(serverId) {
  return request.get(`/portfolio/servers/${serverId}/proxy/unified-orders/history`)
}

// 撤销指定订单
export function cancelServerUnifiedOrder(serverId, brokerId, orderId) {
  return request.delete(`/portfolio/servers/${serverId}/proxy/unified-orders/${encodeURIComponent(brokerId)}/${encodeURIComponent(orderId)}`)
}

// 全部撤单（由服务端统一处理）
export function cancelServerUnifiedOrdersAll(serverId) {
  return request.post(`/portfolio/servers/${serverId}/proxy/unified-orders/cancel-all`)
}

// ========== Emergency Close API (紧急平仓/抄底) ==========
export function emergencyCloseAll(serverId, broker, accId, ratio) {
  let url = '/emergency/close-all'
  const params = []
  if (serverId) params.push(`serverId=${serverId}`)
  if (broker) params.push(`broker=${encodeURIComponent(broker)}`)
  if (accId) params.push(`accId=${encodeURIComponent(accId)}`)
  if (ratio && Number(ratio) > 0 && Number(ratio) !== 1) params.push(`ratio=${encodeURIComponent(ratio)}`)
  if (params.length) url += '?' + params.join('&')
  return request.post(url)
}

export function closePortfolioPositions(serverId, broker, accId, positions = []) {
  let url = '/portfolio/positions/close'
  const params = []
  if (serverId) params.push(`serverId=${serverId}`)
  if (broker) params.push(`broker=${encodeURIComponent(broker)}`)
  if (accId) params.push(`accId=${encodeURIComponent(accId)}`)
  if (params.length) url += '?' + params.join('&')
  return request.post(url, positions)
}

export function emergencyRebuyAll(serverId, broker, accId, ratio) {
  let url = '/emergency/rebuy-all'
  const params = []
  if (serverId) params.push(`serverId=${serverId}`)
  if (broker) params.push(`broker=${encodeURIComponent(broker)}`)
  if (accId) params.push(`accId=${encodeURIComponent(accId)}`)
  if (ratio != null) params.push(`ratio=${ratio}`)
  if (params.length) url += '?' + params.join('&')
  return request.post(url)
}

// 一键对齐批量下单（通过中间件 batch-order，不传价格）
export function portfolioAlignBatchOrder(orders) {
  return request.post('/portfolio/align/batch-order', orders)
}

export function getEmergencyPending(serverId, broker, accId) {
  let url = '/emergency/pending'
  const params = []
  if (serverId) params.push(`serverId=${serverId}`)
  if (broker) params.push(`broker=${encodeURIComponent(broker)}`)
  if (accId) params.push(`accId=${encodeURIComponent(accId)}`)
  if (params.length) url += '?' + params.join('&')
  return request.get(url)
}

export function cancelEmergencyPending(serverId, broker, accId) {
  let url = '/emergency/cancel'
  const params = []
  if (serverId) params.push(`serverId=${serverId}`)
  if (broker) params.push(`broker=${encodeURIComponent(broker)}`)
  if (accId) params.push(`accId=${encodeURIComponent(accId)}`)
  if (params.length) url += '?' + params.join('&')
  return request.post(url)
}

// ========== IB Trade API (IB下单页面) ==========

// 获取IB服务器列表
export function getIBTradeServers() {
  return request.get('/ib-trade/servers')
}

// 获取实时行情（通过行情服务，包含盘前盘后夜盘价格）
export function getIBTradeQuote(symbol) {
  return request.get('/ib-trade/quote', { params: { symbol } })
}

// 获取市场概览行情（支持多个代码，包含盘前盘后夜盘价格）
export function getMarketOverview(codes, options = {}) {
  const {
    realtime = false,
    params = {},
    headers = {},
    ...restOptions
  } = options || {}

  const requestConfig = {
    ...restOptions,
    params: {
      codes,
      ...params
    }
  }

  if (realtime) {
    requestConfig.params._t = Date.now()
    requestConfig.headers = {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0',
      ...headers
    }
  } else if (Object.keys(headers).length) {
    requestConfig.headers = headers
  }

  return request.get('/ib-trade/market-overview', requestConfig)
}

// 临时港股行情专用：固定走支持 lot_size 的后端专用接口
export function getHkFixedMarketOverview(codes, options = {}) {
  const {
    params = {},
    headers = {},
    ...restOptions
  } = options || {}

  return request.get('/ib-trade/market-overview/hk-fixed', {
    ...restOptions,
    params: {
      codes,
      _t: Date.now(),
      ...params
    },
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0',
      ...headers
    }
  })
}

// 获取盘口数据（买卖五档）
export function getOrderbook(code) {
  return request.get('/ib-trade/orderbook', { params: { code } })
}

// 获取 IB K 线/分时数据
export function getIBTradeKline(params) {
  return request.get('/ib-trade/kline', { params })
}

// 获取IB连接状态（指定服务器）
export function getIBTradeStatus(serverId, broker = 'ib') {
  return request.get('/ib-trade/status', { params: { serverId, broker } })
}

// 获取交易时段信息
export function getIBTradeSessions(serverId) {
  return request.get('/ib-trade/sessions', { params: { serverId } })
}

// IB下单（通过中间件）
export function placeIBTradeOrder(serverId, orderData, broker = 'ib') {
  return request.post(`/ib-trade/order?serverId=${serverId}&broker=${encodeURIComponent(broker)}`, orderData)
}

// IB 批量下单（多账户）
export function placeIBTradeOrderBatch(orders, broker = 'ib') {
  return request.post(`/ib-trade/order/batch?broker=${encodeURIComponent(broker)}`, { orders })
}

// 更新百分比基数配置
export function updateIBTradeBaseConfig(serverId, baseAmount, baseType = 'FIXED') {
  return request.put('/ib-trade/base-config', null, {
    params: { serverId, baseAmount, baseType }
  })
}

// ========== MT5 交易接口 ==========

// 服务器配置管理
export function getMT5Servers() {
  return request.get('/mt5/servers')
}

export function addMT5Server(server) {
  return request.post('/mt5/servers', server)
}

export function updateMT5Server(id, server) {
  return request.put(`/mt5/servers/${id}`, server)
}

export function deleteMT5Server(id) {
  return request.delete(`/mt5/servers/${id}`)
}

export function checkMT5ServerStatus(id) {
  return request.get(`/mt5/servers/${id}/status`)
}

// 终端管理
export function getMT5Terminals(serverId) {
  return request.get(`/mt5/servers/${serverId}/terminals`)
}

export function getMT5Terminal(serverId) {
  return request.get(`/mt5/servers/${serverId}/terminal`)
}

export function switchMT5Terminal(serverId, path) {
  return request.post(`/mt5/servers/${serverId}/terminal`, { path })
}

// 账户和持仓
export function getMT5Account(serverId) {
  return request.get(`/mt5/servers/${serverId}/account`)
}

export function getMT5Positions(serverId) {
  return request.get(`/mt5/servers/${serverId}/positions`)
}

export function getMT5History(serverId, params) {
  return request.get(`/mt5/servers/${serverId}/history`, { params })
}

export function getMT5Quotes(serverId) {
  return request.get(`/mt5/servers/${serverId}/quotes`)
}

// 交易操作
export function placeMT5Order(serverId, orderData) {
  return request.post(`/mt5/servers/${serverId}/order`, orderData)
}

export function closeMT5Position(serverId, ticket) {
  return request.post(`/mt5/servers/${serverId}/close/${ticket}`)
}

export function getMT5Stats(serverId) {
  return request.get(`/mt5/servers/${serverId}/stats`)
}

// MT5 品种映射
export function getMT5SymbolMaps(params) {
  return request.get('/mt5/symbol-maps', { params })
}

export function createMT5SymbolMap(data) {
  return request.post('/mt5/symbol-maps', data)
}

export function updateMT5SymbolMap(id, data) {
  return request.put(`/mt5/symbol-maps/${id}`, data)
}

export function deleteMT5SymbolMap(id) {
  return request.delete(`/mt5/symbol-maps/${id}`)
}

// MT5 跟单配置
export function getMT5CopyConfig() {
  return request.get('/mt5/copy-config')
}

export function saveMT5CopyConfig(config) {
  return request.post('/mt5/copy-config', config)
}

export function switchMT5SourceMode(sourceMode) {
  return request.post('/mt5/copy-config/source-mode', { sourceMode })
}

export function toggleMT5Copy(enabled) {
  return request.post('/mt5/copy-config/toggle', { enabled })
}

// MT5 跟单目标
export function getMT5CopyTargets() {
  return request.get('/mt5/copy-targets')
}

export function getAvailableServers() {
  return request.get('/mt5/copy-targets/available-servers')
}

export function addMT5CopyTarget(target) {
  return request.post('/mt5/copy-targets', target)
}

export function updateMT5CopyTarget(id, target) {
  return request.put(`/mt5/copy-targets/${id}`, target)
}

export function deleteMT5CopyTarget(id) {
  return request.delete(`/mt5/copy-targets/${id}`)
}

// ========== 每日盈利 API ==========

// 获取每日汇总数据（用于曲线图）
export function getDailyProfitSummary(startDate, endDate) {
  return request.get('/daily-profit/summary', { params: { startDate, endDate } })
}

// 获取详细记录列表
export function getDailyProfitList(startDate, endDate) {
  return request.get('/daily-profit/list', { params: { startDate, endDate } })
}

// 获取指定日期的详细记录
export function getDailyProfitDetail(date) {
  return request.get('/daily-profit/detail', { params: { date } })
}

// 获取日历数据
export function getDailyProfitCalendar(year, month) {
  return request.get('/daily-profit/calendar', { params: { year, month } })
}

// 手动触发采集
export function collectDailyProfit(date) {
  const params = date ? { date } : {}
  return request.post('/daily-profit/collect', null, { params })
}

// ========== 风控中心 API ==========

export function getRiskOverview() {
  return request.get('/risk/overview')
}

export function getRiskHealth() {
  return request.get('/risk/health')
}

export function getRiskTriggers(limit = 100) {
  return request.get('/risk/triggers', { params: { limit } })
}

export function getRiskPendingActions(limit = 100) {
  return request.get('/risk/pending-actions', { params: { limit } })
}

export function getRiskPositionRisks() {
  return request.get('/risk/position-risks')
}

export function getRiskPositionVolatility() {
  return request.get('/risk/position-volatility')
}

export function runRiskCheck(persist = true) {
  return request.post('/risk/run', null, { params: { persist } })
}

export function getRiskReplay(days = 60) {
  return request.get('/risk/replay', { params: { days } })
}

export function getRiskConfig() {
  return request.get('/risk/config')
}

export function evaluateRiskPolicy(payload) {
  return request.post('/risk/policy/evaluate', payload || {})
}

export function saveRiskConfig(payload) {
  return request.put('/risk/config', payload)
}

export function confirmRiskPendingAction(id) {
  return request.post(`/risk/pending-actions/${id}/confirm`)
}

export function cancelRiskPendingAction(id) {
  return request.post(`/risk/pending-actions/${id}/cancel`)
}

export function startPortfolioAiAnalysis(payload) {
  return request.post('/risk/portfolio-analysis/start', payload)
}

export function getPortfolioAiAnalysisStatus(taskId) {
  return request.get(`/risk/portfolio-analysis/status/${taskId}`)
}

// ==================== 推送通知 ====================

// 获取所有通知渠道状态
export function getNotifyChannels() {
  return request.get('/notify/channels')
}

// 获取 Bark 配置
export function getBarkConfig() {
  return request.get('/notify/config/bark')
}

// 更新 Bark 配置
export function updateBarkConfig(data) {
  return request.post('/notify/config/bark', data)
}

// 获取邮箱配置
export function getEmailConfig() {
  return request.get('/notify/config/email')
}

// 更新邮箱配置
export function updateEmailConfig(data) {
  return request.post('/notify/config/email', data)
}

// 测试推送
export function testNotify(data) {
  return request.post('/notify/test', data)
}

// ==================== Web Push ====================

// 获取 VAPID 公钥
export function getVapidPublicKey() {
  return request.get('/notify/webpush/vapid-key')
}

// 订阅 Web Push
export function subscribeWebPush(data) {
  return request.post('/notify/webpush/subscribe', data)
}

// 取消订阅
export function unsubscribeWebPush(endpoint) {
  return request.post('/notify/webpush/unsubscribe', { endpoint })
}

// 获取订阅列表
export function getWebPushSubscriptions() {
  return request.get('/notify/webpush/subscriptions')
}

// 测试推送到指定浏览器设备
export function testWebPushTargets(data) {
  return request.post('/notify/webpush/test-targets', data)
}

// 测试推送到指定 Bark 设备
export function testBarkTargets(data) {
  return request.post('/notify/bark/test-targets', data)
}

// 获取 Web Push 配置
export function getWebPushConfig() {
  return request.get('/notify/config/webpush')
}

// 更新 Web Push 配置
export function updateWebPushConfig(data) {
  return request.post('/notify/config/webpush', data)
}

// 生成 VAPID 密钥对
export function generateVapidKeys() {
  return request.post('/notify/webpush/generate-vapid')
}

// 查询 PWA 消息历史
export function getPwaMessageHistory(params) {
  return request.get('/notify/webpush/messages', { params })
}

// ==================== 组合数据 API ====================

// 获取所有雪球组合
export function getXueqiuPortfolios() {
  return request.get('/portfolio-data/xueqiu/portfolios')
}

// 获取单个雪球组合信息
export function getXueqiuPortfolio(portfolioId) {
  return request.get(`/portfolio-data/xueqiu/portfolio/${portfolioId}`)
}

// 获取雪球组合调仓记录
export function getXueqiuRebalancing(portfolioId, count = 5) {
  return request.get(`/portfolio-data/xueqiu/rebalancing/${portfolioId}`, { params: { count } })
}

// 获取雪球组合持仓
export function getXueqiuPositions(portfolioId) {
  return request.get(`/portfolio-data/xueqiu/positions/${portfolioId}`)
}

// 获取所有富途组合
export function getFutuPortfolios() {
  return request.get('/portfolio-data/futu/portfolios')
}

// 获取富途组合持仓
export function getFutuPortfolioPositions(portfolioId) {
  return request.get(`/portfolio-data/futu/positions/${portfolioId}`)
}

// 获取富途组合基本信息
export function getFutuInfo(portfolioId) {
  return request.get(`/portfolio-data/futu/info/${portfolioId}`)
}

// 聚合获取有分配金额组合的持仓 + 行情
export function getAllocatedPortfolioPositionsQuotes(payload) {
  return request.post('/portfolio-data/allocated-positions-quotes', payload)
}

// 获取股票日线均线（中间件透传，返回原始 success/data 结构）
export function getTickerMa(symbol, params = {}) {
  const token = localStorage.getItem('token')
  return axios.get(`/api/ticker/${encodeURIComponent(symbol)}/ma`, {
    params,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  }).then(res => res.data)
}

// 获取 erpfox 组合数据
export function getErpfoxPortfolios() {
  return request.get('/portfolio-data/erpfox')
}

// ========== 自定义组合 API ==========

// 自定义组合基本信息（对齐雪球契约）
export function getCustomPortfolio(portfolioId) {
  return request.get(`/portfolio-data/custom/info/${portfolioId}`)
}

// 自定义组合持仓
export function getCustomPositions(portfolioId) {
  return request.get(`/portfolio-data/custom/positions/${portfolioId}`)
}

// 自定义组合调仓记录
export function getCustomRebalancing(portfolioId, count = 5) {
  return request.get(`/portfolio-data/custom/rebalancing/${portfolioId}`, { params: { count } })
}

// 自定义组合持仓 CRUD
export function getCustomPortfolioHoldings(portfolioId) {
  return request.get('/custom-portfolio/holdings', { params: { portfolioId } })
}

export function createCustomPortfolioHolding(data) {
  return request.post('/custom-portfolio/holdings', data)
}

export function updateCustomPortfolioHolding(id, data) {
  return request.put(`/custom-portfolio/holdings/${id}`, data)
}

export function deleteCustomPortfolioHolding(id) {
  return request.delete(`/custom-portfolio/holdings/${id}`)
}

// ========== 订单同步跟单配置 API ==========

export function getOrderSyncConfigs() {
  return request.get('/order-sync-config/list')
}

export function saveOrderSyncConfig(data) {
  return request.post('/order-sync-config/save', data)
}

export function deleteOrderSyncConfig(id) {
  return request.delete(`/order-sync-config/${id}`)
}

// ========== IB 单笔止盈配置 API ==========

export function getIBTakeProfitConfig() {
  return request.get('/ib-take-profit/config')
}

export function updateIBTakeProfitConfig(data) {
  return request.put('/ib-take-profit/config', data)
}

export function getIBTakeProfitOrders() {
  return request.get('/ib-take-profit/orders')
}

// ========== 自动跟单配置 API ==========

export function getAutoFollowConfig() {
  return request.get('/auto-follow/config')
}

export function saveAutoFollowConfig(data) {
  return request.post('/auto-follow/config', data)
}

export function initAutoFollowBaseline() {
  return request.post('/auto-follow/baseline/init')
}

export function pollAutoFollowOnce() {
  return request.post('/auto-follow/poll-once')
}

export default request
